import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraInput, SpecraOverlayDrawer, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { portfolioClient } from '../../../lib/connect/clients';
import { PORTFOLIO_ECONOMICS_KEY } from '../hooks/usePortfolioEconomics';
export function OpportunityValueEditor({ opportunityId }) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [source, setSource] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [kind, setKind] = useState('deal');
    const cache = useQueryClient();
    const busy = useRef(false);
    const valid = /^\d+(\.\d{1,2})?$/.test(amount) && !!source.trim() && /^[A-Z]{3}$/.test(currency);
    const mutation = useMutation({
        mutationFn: ()=>portfolioClient.setOpportunityValue({
                opportunityId,
                kind,
                amount,
                currency,
                source: source.trim()
            }),
        onSuccess: async ()=>{
            await Promise.all([
                cache.invalidateQueries({
                    queryKey: PORTFOLIO_ECONOMICS_KEY
                }),
                cache.invalidateQueries({
                    queryKey: [
                        'landscape-strategy'
                    ]
                })
            ]);
            setOpen(false);
        },
        onSettled: ()=>{
            busy.current = false;
        }
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "outline",
                size: "sm",
                "data-testid": Testids.landscapeStrategy.editValue,
                onClick: ()=>setOpen(true),
                children: "Edit value"
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: open,
                onOpenChange: (v)=>{
                    if (!mutation.isPending) setOpen(v);
                },
                title: "Canonical Opportunity value",
                description: "Updates the owning parent-level value for this kind. Active lot values take precedence in economics.",
                children: /*#__PURE__*/ _jsxs("form", {
                    className: "space-y-3",
                    onSubmit: (e)=>{
                        e.preventDefault();
                        if (!valid || busy.current) return;
                        busy.current = true;
                        mutation.mutate();
                    },
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSelect, {
                            "aria-label": "Value kind",
                            value: kind,
                            onChange: (e)=>setKind(e.target.value),
                            children: [
                                'deal',
                                'share',
                                'bid',
                                'award',
                                'revenue'
                            ].map((k)=>/*#__PURE__*/ _jsx("option", {
                                    value: k,
                                    children: k
                                }, k))
                        }),
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Currency",
                            value: currency,
                            onChange: (e)=>setCurrency(e.target.value.toUpperCase()),
                            maxLength: 3
                        }),
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Amount",
                            placeholder: "Amount",
                            "data-testid": Testids.landscapeStrategy.valueAmount,
                            value: amount,
                            onChange: (e)=>setAmount(e.target.value),
                            inputMode: "decimal",
                            required: true
                        }),
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Source",
                            placeholder: "Source",
                            "data-testid": Testids.landscapeStrategy.valueSource,
                            value: source,
                            onChange: (e)=>setSource(e.target.value),
                            required: true
                        }),
                        !valid && /*#__PURE__*/ _jsx("p", {
                            "aria-live": "polite",
                            children: "Enter a nonnegative amount with up to two decimals, a three-letter currency and its source."
                        }),
                        mutation.error && /*#__PURE__*/ _jsx("p", {
                            role: "alert",
                            children: "Could not save this value. Your entries are retained; retry saving."
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            type: "submit",
                            "data-testid": Testids.landscapeStrategy.valueSave,
                            disabled: !valid || mutation.isPending,
                            isLoading: mutation.isPending,
                            children: "Save value"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            type: "button",
                            variant: "ghost",
                            disabled: mutation.isPending,
                            onClick: ()=>setOpen(false),
                            children: "Cancel"
                        })
                    ]
                })
            })
        ]
    });
}
